<template>
  <Teleport defer :to="appendTo">
    <TransitionFade @enter="onEnter">
      <div
        v-if="visible"
        ref="container"
        v-outside-click="outsideClickDirectiveConfig"
        v-bind="{ ...$attrs }"
        class="absolute z-[900] overflow-hidden rounded-xl border border-primary bg-primary"
        :class="[{ '-translate-y-3': isTopPopover }, { 'translate-y-3': isBottomPopover }]"
        :style="{
          left: `${containerStyles.left}px`,
          top: `${containerStyles.top}px`,
          transformOrigin: containerStyles.transformOrigin
        }"
      >
        <slot v-if="visible" />
      </div>
    </TransitionFade>
  </Teleport>
</template>

<script lang="ts" setup>
import { TransitionFade } from '@morev/vue-transitions'
import type { Undefinable } from 'ts-helpers'
import type { OutsideClickOptions } from 'ts-helpers'
import { computed, reactive, ref } from 'vue'
import { getElementAbsolutePosition } from '../lib'

/**
 * left и top работают только, если задан appendTo!
 */
type PropType = {
  appendTo?: string
  dontHideOnOutsideClick?: boolean
  triangleClasses?: string
  alwaysVisible?: boolean
  left?: number
  top?: number
}
type EmitType = {
  (e: 'open'): void
  (e: 'close'): void
}

const props = withDefaults(defineProps<PropType>(), {
  appendTo: 'body',
  dontHideOnOutsideClick: false,
  triangleClasses: 'text-[var(--background-primary)]',
  alwaysVisible: false,
  left: undefined,
  top: undefined
})
const emit = defineEmits<EmitType>()

defineOptions({
  inheritAttrs: false
})

const visible = ref(false)
const container = ref<Undefinable<HTMLElement>>(undefined)
const target = ref<Undefinable<HTMLElement>>(undefined)

type ContainerStyles = {
  top: number | string
  left: number | string
  transformOrigin: 'top' | 'bottom'
}
type ContainerPosition = {
  positionY: 'bottom' | 'top'
  positionX: 'left' | 'right'
}

const containerStyles = reactive<ContainerStyles>({
  top: 0,
  left: 0,
  transformOrigin: 'bottom'
})
const containerPosition = reactive<ContainerPosition>({
  positionY: 'bottom',
  positionX: 'left'
})

const setContainerStyles = (styles: ContainerStyles) => {
  containerStyles.top = styles.top
  containerStyles.left = styles.left
  containerStyles.transformOrigin = styles.transformOrigin
}
const setContainerPosition = (position: ContainerPosition) => {
  containerPosition.positionX = position.positionX
  containerPosition.positionY = position.positionY
}

const isTopPopover = computed(() => containerPosition.positionY === 'top')
const isBottomPopover = computed(() => containerPosition.positionY === 'bottom')
// const isRightPopover = computed(() => containerPosition.positionX === 'right')
// const isLeftPopover = computed(() => containerPosition.positionX === 'left')

const onEnter = () => {
  if (props.appendTo !== 'body') {
    setContainerStyles({
      top: props.top ? props.top : 0,
      left: props.left ? props.left : 0,
      transformOrigin: 'bottom'
    } as ContainerStyles)

    setContainerPosition({
      positionY: 'bottom',
      positionX: 'left'
    } as ContainerPosition)

    return
  }

  const calculatedPosition = getElementAbsolutePosition(container.value, target.value)

  calculatedPosition && setContainerStyles(calculatedPosition as ContainerStyles)
  calculatedPosition && setContainerPosition(calculatedPosition as ContainerPosition)
}

const show = (event: MouseEvent) => {
  if (props.alwaysVisible) return
  event.target && (target.value = event.target as HTMLElement)
  outsideClickDirectiveConfig.ignoreElements = [target.value!]

  setTimeout(() => {
    visible.value = true
    emit('open')
  })
}

const hide = () => {
  if (props.alwaysVisible) return

  visible.value = false
  target.value = undefined
  container.value = undefined
  emit('close')
}

const toggle = (event: MouseEvent) => {
  if (props.alwaysVisible) return

  visible.value ? hide() : show(event)
}

let outsideClickDirectiveConfig: OutsideClickOptions = {
  ignoreElements: [],
  handler: hide
}

defineExpose({
  show,
  hide,
  toggle
})
</script>
